import { useEffect, useState } from 'react';

interface StickyProductBarProps {
  name: string;
  price: number;
  oldPrice: number;
  color: string;
  inStock: boolean;
  onBuy: () => void;
  onCart: () => void;
}

const StickyProductBar = ({ name, price, oldPrice, color, inStock, onBuy, onCart }: StickyProductBarProps) => {
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 420);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${visible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}
      style={{
        background: 'rgba(5,5,8,0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${color}40`,
        boxShadow: `0 4px 30px ${color}20`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center gap-4">
        {/* Product Name */}
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-1 h-8 rounded-full shrink-0"
            style={{ background: color, boxShadow: `0 0 8px ${color}` }}
          />
          <span
            className="font-orbitron font-bold text-sm text-white whitespace-nowrap hidden md:block"
            style={{ textShadow: `0 0 10px ${color}40` }}
          >
            {name}
          </span>
        </div>

        {/* Search */}
        <div
          className="flex-1 max-w-xs hidden lg:flex items-center gap-2 px-3 py-2 rounded-full"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-search-line text-white/40 text-xs" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск товаров..."
            className="bg-transparent outline-none text-white/70 text-xs font-mono w-full placeholder:text-white/25"
          />
        </div>

        <div className="flex-1" />

        {/* Price */}
        <div className="flex items-baseline gap-2 shrink-0">
          <span
            className="font-orbitron font-black text-xl"
            style={{ color, textShadow: `0 0 12px ${color}` }}
          >
            {price.toLocaleString()}₽
          </span>
          <span className="font-mono text-xs text-white/30 line-through hidden sm:block">
            {oldPrice.toLocaleString()}₽
          </span>
        </div>

        {/* Buttons */}
        <button
          onClick={onBuy}
          className="whitespace-nowrap px-5 py-2 rounded-full font-orbitron font-bold text-xs tracking-wider cursor-pointer transition-all duration-300"
          style={{
            background: color,
            color: '#050508',
            boxShadow: `0 0 15px ${color}80`,
          }}
        >
          КУПИТЬ
        </button>
        <button
          onClick={onCart}
          className="whitespace-nowrap px-5 py-2 rounded-full font-mono text-xs tracking-wider cursor-pointer transition-all duration-300 hidden sm:block"
          style={{
            border: `1px solid ${color}`,
            color,
          }}
        >
          В КОРЗИНУ
        </button>

        {/* Stock status */}
        <div className="hidden xl:flex items-center gap-1.5 shrink-0">
          <div
            className="w-2 h-2 rounded-full"
            style={{
              background: inStock ? '#39ff14' : '#ff2d78',
              boxShadow: inStock ? '0 0 6px #39ff14' : '0 0 6px #ff2d78',
            }}
          />
          <span className="font-mono text-xs" style={{ color: inStock ? '#39ff14' : '#ff2d78' }}>
            {inStock ? 'В наличии' : 'Нет в наличии'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default StickyProductBar;
